<script lang="ts">
  import HowToPlayLink from '../shared/HowToPlayLink.svelte';
  import BigScreenNote from '../shared/BigScreenNote.svelte';
  import TvWindowButton from '../shared/TvWindowButton.svelte';
  import OwnSpotifyApp from './OwnSpotifyApp.svelte';
  import { SPOTIFY_INVITE_COPY, type MusicService } from '@banger/shared';
  let { busy = false, error = '', setupMissing = false, customActive = false, service = 'apple', onhost, onownlogin, onswitchback }: {
    busy?: boolean; error?: string; setupMissing?: boolean; customActive?: boolean;
    /** The service picked last: highlighted when choosing. */
    service?: MusicService;
    onhost: (service: MusicService) => void;
    /** After the walkthrough saved a custom Client ID: log in with it (and create the room). */
    onownlogin: () => void;
    /** "Switch back to default": forget the custom Client ID. */
    onswitchback: () => void;
  } = $props();
  let ownOpen = $state(false);
  /** Host a game → choose a music service → (Spotify) the invite-only note and its options. */
  let step = $state<'home' | 'choose' | 'spotify'>('home');
</script>

<main>
  <div class="ball"><span class="disco-ball"></span></div>
  <h1 class="wordmark">BANGER!</h1>
  <p class="tag">The music bingo party game</p>
  {#if step === 'home'}
    <button class="btn-neon host" onclick={() => (step = 'choose')} disabled={busy}>{busy ? 'Starting…' : 'Host a game'}</button>
  {:else if step === 'choose'}
    <p class="ask">Play the music with…</p>
    <div class="services">
      <button class="btn-neon svc" class:last={service === 'apple'} onclick={() => onhost('apple')} disabled={busy}>
        {busy ? 'Starting…' : 'Apple Music'}
      </button>
      <button class="btn-neon svc" class:last={service === 'spotify'} onclick={() => (step = 'spotify')} disabled={busy}>Spotify</button>
    </div>
    <button type="button" class="linkish" onclick={() => (step = 'home')}>Back</button>
    <p class="note">Keep your own phone's music app out of sight — it can show what's playing.</p>
    <p class="note">Hearing it from the wrong speakers? Switch your computer's sound output to the TV.</p>
    <BigScreenNote variant="host" />
  {:else}
    <p class="invite">{SPOTIFY_INVITE_COPY}</p>
    <div class="services">
      {#if !setupMissing}
        <button class="btn-neon svc" onclick={() => onhost('spotify')} disabled={busy}>{busy ? 'Starting…' : 'Continue with Spotify'}</button>
      {/if}
      <button class="btn-neon svc alt" onclick={() => onhost('apple')} disabled={busy}>Use Apple Music</button>
    </div>
    {#if !setupMissing}<p class="note">Continue with Spotify only works for invited accounts.</p>{/if}
    {#if customActive}
      <p class="own">
        <button type="button" class="linkish" onclick={() => (ownOpen = true)}>Using your own Spotify app (Edit Client ID)</button> ·
        <button type="button" class="linkish" onclick={onswitchback}>Switch back to default</button>
      </p>
    {:else}
      <button type="button" class="ownpill" onclick={() => (ownOpen = true)}>🛠️ Use your own Spotify app (advanced)</button>
    {/if}
    {#if setupMissing}
      <p class="note">Running it yourself? Add <code>VITE_SPOTIFY_CLIENT_ID</code> to <code>client/.env</code> (see <code>client/.env.example</code>) and restart.</p>
    {/if}
    <button type="button" class="linkish" onclick={() => (step = 'choose')}>Back</button>
  {/if}
  {#if step === 'home'}<HowToPlayLink cls="howto" />{/if}
  {#if error}<p class="err">{error}</p>{/if}
  <div class="tools"><TvWindowButton size="small" /></div>
</main>
{#if ownOpen}<OwnSpotifyApp onclose={() => (ownOpen = false)} onlogin={() => { ownOpen = false; onownlogin(); }} />{/if}

<style>
  main { position: relative; min-height: 100%; display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 1rem; padding: 2rem; text-align: center; }
  .ball { font-size: clamp(90px, 14vw, 170px); }
  h1 { margin: 0; font-size: clamp(4rem, 14vw, 10rem); line-height: 1; }
  .tag { margin: 0 0 1rem; font-size: clamp(1.1rem, 2.4vw, 1.8rem); color: var(--cyan); text-shadow: 0 0 12px rgba(125, 249, 255, 0.6); }
  .host { font-size: clamp(1.5rem, 3.4vw, 2.6rem); padding: 0.5em 1.8em; }
  .ask { margin: 0; font-size: clamp(1.2rem, 2.4vw, 1.8rem); font-weight: 700; }
  .invite { margin: 0; max-width: 34ch; font-size: clamp(1.1rem, 2.2vw, 1.6rem); font-weight: 700; }
  .services { display: flex; flex-wrap: wrap; justify-content: center; gap: 1rem; }
  .svc { font-size: clamp(1.2rem, 2.6vw, 2rem); padding: 0.5em 1.5em; }
  .svc.last { box-shadow: 0 0 26px rgba(255, 79, 216, 0.75); }
  .svc.alt { opacity: 0.9; }
  .ownpill {
    font-family: inherit; font-weight: 800; font-size: clamp(1rem, 1.8vw, 1.3rem); color: var(--cyan); cursor: pointer;
    border-radius: 999px; padding: 0.45em 1.4em; min-height: 48px;
    background: rgba(125, 249, 255, 0.08); border: 2px solid rgba(125, 249, 255, 0.6); box-shadow: 0 0 14px rgba(125, 249, 255, 0.3);
  }
  .ownpill:hover, .ownpill:focus-visible { box-shadow: 0 0 22px rgba(125, 249, 255, 0.6); }
  .note { margin: 0; max-width: 40ch; font-size: clamp(1rem, 1.7vw, 1.3rem); opacity: 0.75; }
  :global(.howto-link.howto) { font-size: clamp(1.1rem, 2vw, 1.6rem); }
  .tools { position: absolute; left: 1.5rem; bottom: 1rem; }
  .err { color: #ff8fa3; font-weight: 700; margin: 0; }
  .own { margin: 0; font-size: clamp(0.9rem, 1.2vw, 1.1rem); opacity: 0.7; }
  .linkish { font: inherit; color: var(--cyan); background: none; border: 0; padding: 0 0.2em; cursor: pointer; text-decoration: underline; display: inline-flex; align-items: center; min-height: 48px; }
  .linkish:hover, .linkish:focus-visible { opacity: 1; text-shadow: 0 0 10px rgba(125, 249, 255, 0.6); }
</style>
